import { DeepMap } from 'react-hook-form';
import { JobsiteUpdateInput } from 'apollo/generated/client-operations';
import { ensureNonUndefinedFields, isNotEmpty } from 'utils';
import { FormInputTypes, TypedFormInputs } from 'components/form';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import { Jobsite } from './types';
import {
  getGeofencingSectionDefaultValues,
  getGeofencingSectionInputs,
  getGeofencingSectionUpdateInput,
} from './geofencingSection';
import {
  getGatewaysSectionDefaultValues,
  getGatewaysSectionInputs,
  getGatewaysSectionUpdateInput,
} from './gatewaysSection';

// Gateways are absent from the form while the site layout designer is on.
// They still back mobile check-in, stations and swipe filters; only their
// editor is hidden.
export type EditJobsiteConfigTabFormData = Pick<
  EditJobsiteConfigurationFormData,
  'geofencing'
> &
  Partial<Pick<EditJobsiteConfigurationFormData, 'gateways'>>;

type GetFormInputsArgs = {
  jobsite: Jobsite;
  /** False while the site layout designer is on, which hides the gateways editor. */
  showGateways: boolean;
};

export const getFormInputs = (
  args: GetFormInputsArgs,
): TypedFormInputs<EditJobsiteConfigTabFormData> => {
  const { jobsite, showGateways } = args;

  const geofencingSectionInputs = getGeofencingSectionInputs({ jobsite });
  const gatewaysSectionInputs = getGatewaysSectionInputs({ jobsite });

  return {
    geofencing: {
      element: FormInputTypes.Section,
      layout: '',
      elementProps: {
        title: 'Geofencing',
        details:
          'Add and manage geographical areas to accurately gate site access via mobile app',
      },
      children: geofencingSectionInputs,
    },
    ...(showGateways && {
      gateways: {
        element: FormInputTypes.Section,
        layout: '',
        elementProps: {
          title: 'Gateways',
          details:
            'Define a list of named gateways for this site. ' +
            'Each name should reference the physical entry it represents ' +
            '(e.g. "North Gate", "South Entry", "Loading Dock"). ' +
            'Existing gateways can be renamed at any time.',
        },
        children: gatewaysSectionInputs,
      },
    }),
  };
};

type GetDefaultValuesArgs = {
  jobsite: Jobsite;
  showGateways: boolean;
};

export const getDefaultValues = (
  args: GetDefaultValuesArgs,
): EditJobsiteConfigTabFormData => {
  const { jobsite, showGateways } = args;
  const geofencing = getGeofencingSectionDefaultValues(jobsite);
  return showGateways
    ? { geofencing, gateways: getGatewaysSectionDefaultValues(jobsite) }
    : { geofencing };
};

const getAppCheckInModuleInput = (
  jobsite: Jobsite,
  data: EditJobsiteConfigTabFormData,
  dirtyFields: DeepMap<EditJobsiteConfigTabFormData, true>,
): JobsiteUpdateInput['appCheckInModule'] => {
  const updateInput = getGeofencingSectionUpdateInput(
    data.geofencing,
    dirtyFields.geofencing,
  );
  const hasChanged = isNotEmpty(updateInput);

  return hasChanged
    ? {
        name: `${jobsite.name} - Check In`,
        slug: 'check-in',
        isEnabled: true,
        ...updateInput,
      }
    : undefined;
};

export const getJobsiteUpdateInput = (
  jobsite: Jobsite,
  data: EditJobsiteConfigTabFormData,
  dirtyFields: DeepMap<EditJobsiteConfigTabFormData, true>,
): JobsiteUpdateInput => {
  return ensureNonUndefinedFields<JobsiteUpdateInput>({
    jobsiteId: jobsite.jobsiteId,
    appCheckInModule: getAppCheckInModuleInput(jobsite, data, dirtyFields),
    ...(data.gateways &&
      getGatewaysSectionUpdateInput(data.gateways, dirtyFields.gateways)),
  });
};
